import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { RecentAnswer } from "winslow-core";
import { convertUTCtoLocal, toMMDD } from "winslow-core";

ChartJS.register(...registerables);

type EscalationsChartProps = {
  answers: RecentAnswer[];
  startDate: Date; // inclusive
  endDate: Date; // inclusive
};

const ESCALATION_BUCKETS = ["Escalated"] as const;
const ESCALATION_COLORS: Record<(typeof ESCALATION_BUCKETS)[number], string> = {
  Escalated: "#EF4444",
};

export const EscalationsChart = ({
  answers,
  startDate,
  endDate,
}: EscalationsChartProps) => {
  const calculateDaysBetweenDates = (date1: Date, date2: Date) => {
    const differenceMillis = date2.getTime() - date1.getTime();
    return Math.round(differenceMillis / (1000 * 60 * 60 * 24));
  };

  const getBucketIndexForDate = (forDate: Date): number => {
    return calculateDaysBetweenDates(startDate, forDate);
  };

  const labels = (() => {
    const rangeDays = calculateDaysBetweenDates(startDate, endDate);
    const l: string[] = [];
    for (let i = 0; i <= rangeDays; i++) {
      const d = new Date(startDate.getTime());
      d.setDate(startDate.getDate() + i);
      l.push(toMMDD(d));
    }
    return l;
  })();

  // Initialize buckets: dayIndex -> { escalationLabel -> count }
  const rangeDays = calculateDaysBetweenDates(startDate, endDate);
  const buckets: Array<Record<(typeof ESCALATION_BUCKETS)[number], number>> =
    Array.from({ length: rangeDays + 1 }, () => ({
      Escalated: 0,
    }));

  // Aggregate
  answers.forEach((ra) => {
    const createdAt = new Date(ra.createdAt);
    const localDate = convertUTCtoLocal(createdAt);
    if (localDate < startDate || localDate > endDate) return;
    const dayIdx = getBucketIndexForDate(localDate);

    // Check if this answer required escalation

    const key: (typeof ESCALATION_BUCKETS)[number] = "Escalated";
    buckets[dayIdx][key] = (buckets[dayIdx][key] || 0) + 1;
  });

  const datasets = ESCALATION_BUCKETS.map((key) => ({
    label: key,
    data: buckets.map((b) => b[key] || 0),
    backgroundColor: ESCALATION_COLORS[key],
    borderColor: ESCALATION_COLORS[key],
    stack: "escalations",
  }));

  const data = { labels, datasets } as any;

  const options = {
    plugins: {
      title: { display: true, text: "Escalations (Last 30 Days)" },
      tooltip: { mode: "index" as const, intersect: false },
      legend: { display: true, position: "right" as const },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        ticks: {
          autoSkip: false,
          maxRotation: 90 as const,
          minRotation: 90 as const,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <Chart type="bar" height="300px" data={data} options={options} />
  );
};

export default EscalationsChart;
